import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Brain,
  Sparkles,
  Users,
  Clock,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { useScrollFadeIn, fadeCls, fadeStyle } from "@/hooks/useScrollFadeIn";

function BadgePill({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-foreground/80">
      <Check className="h-3 w-3 text-primary" />
      {label}
    </span>
  );
}

export default function RelarkHero() {
  const { t } = useTranslation("ui");
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollFadeIn(0.05);
  const [searchParams] = useSearchParams();

  // A/B test: ?v=b activates compliance-variant; sticky via localStorage
  const [variant, setVariant] = useState<"a" | "b">("a");
  useEffect(() => {
    const fromUrl = searchParams.get("v");
    let chosen: "a" | "b" = "a";
    if (fromUrl === "a" || fromUrl === "b") {
      chosen = fromUrl;
      try { localStorage.setItem("relark_hero_variant", chosen); } catch {}
    } else {
      try {
        const stored = localStorage.getItem("relark_hero_variant");
        if (stored === "a" || stored === "b") chosen = stored;
      } catch {}
    }
    setVariant(chosen);
    trackEvent("hero_variant_view", { variant: chosen });
  }, [searchParams]);

  const headline = variant === "b"
    ? t("relark.hero.variantB.headline")
    : t("relark.hero.headline");
  const sub = variant === "b"
    ? t("relark.hero.variantB.sub")
    : t("relark.hero.sub");

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-accent/40 via-background to-background" />
      <div
        ref={ref}
        className="relative mx-auto max-w-7xl px-5 pt-16 pb-16 sm:pt-24 sm:pb-20 min-h-[720px] md:min-h-[760px] lg:min-h-[560px]"
      >
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-12 items-center">
          {/* LEFT: copy */}
          <div className={`text-left ${fadeCls(isVisible)}`}>
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary/90">
              {t("relark.hero.eyebrowLive")}
            </span>
            <h1
              className="whitespace-pre-line text-3xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-[3.25rem]"
              data-speakable="hero-headline"
            >
              {headline}
            </h1>
            <p
              className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg leading-relaxed"
              data-speakable="hero-sub"
            >
              {sub}
            </p>

            <div className="mt-8 flex flex-col items-stretch sm:items-start gap-3 sm:flex-row sm:gap-4">
              <Button
                size="lg"
                className="h-auto min-h-[52px] rounded-full px-8 sm:px-10 py-3.5 text-base font-semibold shadow-md shadow-primary/20"
                onClick={() => {
                  trackEvent("relark_cta_click", { label: "hero_primary", variant });
                  navigate("/onboarding");
                }}
              >
                {t("relark.hero.ctaPrimary")}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-auto min-h-[52px] rounded-full px-8 sm:px-10 py-3.5 text-base font-semibold border-primary/30 hover:bg-primary/5"
                onClick={() => {
                  trackEvent("relark_cta_click", { label: "hero_secondary", variant });
                  document.getElementById("sample-insight")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("relark.hero.ctaSecondary")}
              </Button>
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              {t("relark.hero.ctaPrimaryHint")}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <BadgePill label={t("relark.hero.badges.trial")} />
              <BadgePill label={t("relark.hero.badges.humanOversight")} />
              <BadgePill label={t("relark.hero.badges.euHosted")} />
            </div>
          </div>

          {/* RIGHT: Teams-style mockup */}
          <div
            className={`relative ${fadeCls(isVisible)}`}
            style={fadeStyle(200)}
            role="img"
            aria-label="Eksempel-visning af Relark-output i Teams-lignende format"
          >
            <div className="relative mx-auto max-w-[480px] rounded-2xl border border-border bg-card shadow-2xl shadow-primary/10 overflow-hidden">
              {/* Window chrome */}
              <div className="flex items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                <span className="h-2.5 w-2.5 rounded-full bg-primary/60" />
                <span className="ml-3 text-[11px] text-muted-foreground font-medium">
                  {t("relark.hero.mockup.window")}
                </span>
              </div>

              <div className="p-5 space-y-4">
                {/* Insight card */}
                <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Brain className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                          {t("relark.hero.mockup.archetypeLabel")}
                        </p>
                        <p className="text-sm font-bold text-primary">
                          {t("relark.hero.mockup.archetypeName")}
                        </p>
                      </div>
                    </div>
                    <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-medium text-primary">
                      {t("relark.hero.mockup.diplomatTag")}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-foreground/80">
                    {t("relark.hero.mockup.archetypeDesc")}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    {t("relark.hero.mockup.auditorBadge")}
                  </div>
                </div>

                {/* Copilot suggestion */}
                <div className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
                    <p className="text-[11px] font-semibold text-primary uppercase tracking-wider">
                      {t("relark.hero.mockup.copilotLabel")}
                    </p>
                  </div>
                  <div className="space-y-2 border-l-2 border-primary/30 pl-2.5">
                    <p className="text-[11px] leading-relaxed text-foreground/75 italic">
                      {t("relark.hero.mockup.scriptLine1")}
                    </p>
                    <p className="text-[11px] leading-relaxed text-foreground/75 italic">
                      {t("relark.hero.mockup.scriptLine2")}
                    </p>
                    <p className="text-[11px] leading-relaxed text-foreground/75 italic">
                      {t("relark.hero.mockup.scriptLine3")}
                    </p>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-center">
                    <Users className="h-4 w-4 text-primary mx-auto mb-1" />
                    <p className="text-sm font-bold">{t("relark.hero.mockup.stat1Value")}</p>
                    <p className="text-[10px] text-muted-foreground">{t("relark.hero.mockup.stat1Label")}</p>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-3 text-center">
                    <Clock className="h-4 w-4 text-primary mx-auto mb-1" />
                    <p className="text-sm font-bold">{t("relark.hero.mockup.stat2Value")}</p>
                    <p className="text-[10px] text-muted-foreground">{t("relark.hero.mockup.stat2Label")}</p>
                  </div>
                </div>

                {/* Next meeting strip */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 flex items-center gap-2 text-[11px] text-primary">
                  <Clock className="h-3 w-3" />
                  <span className="font-medium">{t("relark.hero.mockup.nextMeeting")}</span>
                </div>

                {/* ROI strip removed — moved to dedicated "Hvad koster det at vente?" section */}
              </div>
            </div>

            {/* Decorative blur */}
            <div className="absolute -z-10 -top-8 -right-8 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute -z-10 -bottom-12 -left-8 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
}